import React from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Container,
  Typography,
} from "@mui/material";
import AirplaneTicketIcon from "@mui/icons-material/AirplaneTicket";

import { useSearch } from "../context/SearchContext";
import DepartureForm from "./DepartureForm";
import TripTypeForm from "./TripTypeForm";
import PassengerForm from "./PassengerForm";
import AirlineSelection from "./AirlineSelection";
import ClassSelection from "./ClassSelection";
import TravelPreferences from "./TravelPreferences";

const FlightBookingForm = ({ companyCode = "", sessionId = "" }) => {
  const { performSearch, state } = useSearch();
  const { error, isSubmitting } = state;

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      origin: "",
      destination: "",
      directFlightsOnly: false,
      flexibleDates: false,
      tripType: "return",
      departDate: "",
      returnDate: "",
      airline: "all",
      class: "economy",
      adults: 1,
      youths: 0,
      children: 0,
      infants: 0,
      companyCode,
      sessionId,
    },
  });
const onSubmit = async (data) => {
  try {
    const searchUrl = await performSearch(data);
    const isInIframe = window !== window.parent;

    console.log("Search succeeded with URL:", searchUrl);
    console.log("Is in iframe:", isInIframe);

    if (isInIframe) {
      // Ensure we're sending the full, properly encoded URL
      window.parent.postMessage(
        {
          type: "NAVIGATE_TO_SEARCH",
          searchUrl: encodeURIComponent(searchUrl), // Make sure URL is encoded here
          originalUrl: searchUrl, // Keep original for debugging
        },
        "https://demo.globalservicetravel.co.uk" // Use proper target origin in production
      );
    } else {
      // Direct Netlify navigation
      const searchResultsUrl = `${
        window.location.origin
      }/search-results?url=${encodeURIComponent(searchUrl)}`;
      window.location.href = searchResultsUrl;
    }
  } catch (err) {
    console.error("Search failed:", err);
    if (window !== window.parent) {
      window.parent.postMessage(
        {
          type: "SEARCH_FAILED",
          error: err.message,
        },
        "https://demo.globalservicetravel.co.uk" // Use proper target origin in production
      );
    }
    alert("Search failed. Please try again.");
  }
};
  const tripType = watch("tripType");

  const SearchButton = ({ isMobile = false }) => (
    <Button
      variant="contained"
      type="submit"
      size="large"
      fullWidth
      startIcon={
        isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <AirplaneTicketIcon />
        )
      }
      sx={{
        height: 54,
        backgroundColor: isSubmitting ? "grey.400" : "primary.main",
        "&:hover": {
          backgroundColor: isSubmitting ? "grey.400" : "primary.dark",
        },
        display: isMobile
          ? { xs: "flex", md: "none" }
          : { xs: "none", md: "flex" },
      }}
      disabled={isSubmitting}
    >
      {isSubmitting ? "Searching..." : "Search"}
    </Button>
  );

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        mx: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        minHeight: "100vh",
        paddingTop: "10px",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          width: "90%",
          bgcolor: "background.paper",
          maxWidth: "1800px",
          flexGrow: 0,
        }}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "center",
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}

          <TravelPreferences control={control} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Box sx={{ flex: 1 }}>
              <TripTypeForm
                control={control}
                tripType={tripType}
                watch={watch}
                errors={errors}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <DepartureForm
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="flex-start"
            sx={{ width: "100%" }}
          >
            <Box sx={{ width: { xs: "100%", md: "40%" } }}>
              <PassengerForm control={control} />
            </Box>

            <Box
              sx={{
                width: { xs: "100%", md: "60%" },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  width: "100%",
                  flexWrap: { xs: "wrap", md: "nowrap" },
                }}
              >
                <Box sx={{ width: { xs: "calc(50% - 8px)", md: "33%" } }}>
                  <ClassSelection control={control} errors={errors} />
                </Box>

                <Box sx={{ width: { xs: "calc(50% - 8px)", md: "33%" } }}>
                  <AirlineSelection control={control} />
                </Box>

                <Box sx={{ width: { xs: "100%", md: "34%" } }}>
                  <SearchButton />
                </Box>
              </Stack>

              <SearchButton isMobile />
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default FlightBookingForm;
